import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowUpRight, Menu, Phone, X } from 'lucide-react'
import { CONTACT, LOGO_IMAGE } from '@/data/content'

const LINKS = [
  { label: 'Services', to: '/#services' },
  { label: 'Projects', to: '/projects' },
]

const RESOURCE_URL = 'https://trimmedliving.com'

function Logo() {
  return (
    <Link
      to="/"
      aria-label="Trimmed Living, back to top"
      className="flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-400 focus-visible:ring-offset-4 focus-visible:ring-offset-forest-950"
    >
      <img
        src={LOGO_IMAGE}
        alt="Trimmed Living"
        className="h-14 w-auto max-w-[10.5rem] object-contain object-left sm:h-16 sm:max-w-[13rem]"
        loading="eager"
      />
    </Link>
  )
}

export default function Header() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={`sticky top-0 z-50 border-b border-bone-50/10 bg-forest-950 transition-shadow duration-300 ${
        scrolled ? 'shadow-[0_12px_30px_-20px_rgba(0,0,0,0.8)]' : ''
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-2 sm:px-8">
        <Logo />

        <nav aria-label="Primary" className="hidden items-center gap-10 lg:flex">
          {LINKS.map(link => {
            const active = pathname === link.to
            return (
              <Link
                key={link.to}
                to={link.to}
                aria-current={active ? 'page' : undefined}
                className={`rounded-full text-base font-medium transition hover:text-pine-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-400 focus-visible:ring-offset-4 focus-visible:ring-offset-forest-950 ${
                  active ? 'text-pine-400' : 'text-bone-50'
                }`}
              >
                {link.label}
              </Link>
            )
          })}
          <a
            href={RESOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-bone-300/45 px-4 py-2 text-sm font-medium text-bone-100 transition hover:border-bone-300 hover:bg-bone-50/10 hover:text-bone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-400 focus-visible:ring-offset-4 focus-visible:ring-offset-forest-950"
          >
            DIY Guides &amp; Shop
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={CONTACT.phoneHref}
            className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-bone-50/25 px-4 py-2.5 text-sm font-medium tabular-nums text-bone-50 transition hover:border-bone-50/50 hover:bg-bone-50/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-400 focus-visible:ring-offset-2 focus-visible:ring-offset-forest-950 lg:inline-flex"
          >
            <Phone className="h-3.5 w-3.5 shrink-0" />
            <span className="whitespace-nowrap">{CONTACT.phone}</span>
          </a>
          <Link
            to="/#contact"
            className="hidden rounded-full bg-pine px-5 py-2.5 text-sm font-semibold text-bone-50 transition hover:bg-pine-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 focus-visible:ring-offset-2 focus-visible:ring-offset-forest-950 sm:inline-flex"
          >
            Get in Touch
          </Link>
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-bone-50/25 text-bone-50 transition hover:bg-bone-50/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-400 lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Mobile"
          className="border-t border-bone-50/10 bg-forest-950 px-5 pb-5 pt-2 lg:hidden"
        >
          {LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setOpen(false)}
              aria-current={pathname === link.to ? 'page' : undefined}
              className={`block border-b border-bone-50/10 py-3 text-base font-medium ${
                pathname === link.to ? 'text-pine-400' : 'text-bone-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={RESOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-1.5 rounded-full border border-bone-300/45 px-5 py-3 text-sm font-medium text-bone-100"
          >
            DIY Guides &amp; Shop
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href={CONTACT.phoneHref}
            className="mt-4 flex items-center justify-center gap-2 rounded-full border border-bone-50/25 px-5 py-3 text-sm font-medium text-bone-50"
          >
            <Phone className="h-4 w-4" />
            {CONTACT.phone}
          </a>
          <Link
            to="/#contact"
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center justify-center rounded-full bg-pine px-5 py-3 text-sm font-semibold text-bone-50"
          >
            Get in Touch
          </Link>
        </nav>
      )}
    </header>
  )
}
